'use client'

import { motion, useMotionValue, useSpring } from 'framer-motion'
import { useRef, useState } from 'react'

const base =
  'inline-block text-center rounded-none px-8 py-4 text-sm font-medium transition-colors duration-300 magnetic-btn'

/** Editorial CTA styles - solid black primary, outlined secondary (matches Framer reference). */
export const siteButtonClass = {
  primary: `${base} bg-foreground text-background hover:bg-foreground/90`,
  outline: `${base} bg-transparent border border-foreground text-foreground hover:bg-foreground hover:text-background`,
  /** Compact variant for nav */
  outlineCompact:
    'inline-block rounded-none px-6 py-3 text-xs font-medium tracking-[0.15em] uppercase border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors duration-300 magnetic-btn',
} as const

export type SiteButtonVariant = keyof typeof siteButtonClass

export function MagneticButton({
  children,
  className = '',
  onClick,
  variant = 'primary',
}: {
  children: React.ReactNode
  className?: string
  onClick?: () => void
  variant?: 'primary' | 'outline'
}) {
  const ref = useRef<HTMLButtonElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const springX = useSpring(x, { damping: 15, stiffness: 150 })
  const springY = useSpring(y, { damping: 15, stiffness: 150 })

  const variantClass = variant === 'outline' ? siteButtonClass.outline : siteButtonClass.primary

  return (
    <motion.button
      ref={ref}
      type="button"
      style={{ x: springX, y: springY }}
      onMouseMove={(e) => {
        if (!ref.current) return
        const rect = ref.current.getBoundingClientRect()
        x.set((e.clientX - (rect.left + rect.width / 2)) * 0.3)
        y.set((e.clientY - (rect.top + rect.height / 2)) * 0.3)
      }}
      onMouseLeave={() => {
        x.set(0)
        y.set(0)
        setIsHovered(false)
      }}
      onMouseEnter={() => setIsHovered(true)}
      onClick={onClick}
      className={`relative ${variantClass} ${className}`}
    >
      <motion.span animate={{ scale: isHovered ? 1.02 : 1 }}>{children}</motion.span>
    </motion.button>
  )
}

export function MagneticLink({
  children,
  href = '#',
  className = '',
  variant,
}: {
  children: React.ReactNode
  href?: string
  className?: string
  variant?: SiteButtonVariant
}) {
  const ref = useRef<HTMLAnchorElement>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const springX = useSpring(x, { damping: 20, stiffness: 200 })
  const springY = useSpring(y, { damping: 20, stiffness: 200 })

  const variantClass = variant ? siteButtonClass[variant] : ''

  return (
    <motion.a
      ref={ref}
      href={href}
      style={{ x: springX, y: springY }}
      onMouseMove={(e) => {
        if (!ref.current) return
        const rect = ref.current.getBoundingClientRect()
        x.set((e.clientX - (rect.left + rect.width / 2)) * 0.2)
        y.set((e.clientY - (rect.top + rect.height / 2)) * 0.2)
      }}
      onMouseLeave={() => {
        x.set(0)
        y.set(0)
      }}
      className={`${variantClass} ${className}`.trim()}
    >
      {children}
    </motion.a>
  )
}
